<template>
  <div class="modal fade" id="add_board" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">Add New Board</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Board Name</label>
              <input v-model.trim="name" type="text" class="form-control" required />
            </div>

            <label class="form-label">Board Color</label>
            <div class="d-flex align-items-center flex-wrap row-gap-3">
              <div class="theme-colorsset me-3 d-flex align-items-center" v-for="opt in colors" :key="opt.id">
                <input
                  class="visually-hidden"
                  type="radio"
                  name="board-color"
                  :id="opt.id"
                  :value="opt.value"
                  v-model="color"
                  :checked="color === opt.value"
                  @change="color = opt.value"
                />
                <label
                  :for="opt.id"
                  :class="[opt.cls, color === opt.value ? 'ringed' : '']"
                  @click.prevent="color = opt.value"
                  :aria-pressed="color === opt.value"
                  role="button"
                  tabindex="0"
                  @keydown.enter.prevent="color = opt.value"
                  @keydown.space.prevent="color = opt.value"
                />
              </div>
            </div>

            <small class="text-muted d-block mt-2">
              Selected: <span class="text-dark fw-medium">{{ color }}</span>
            </small>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Add New Board
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Modal } from "bootstrap";
import { useBoardStore } from "@/stores/boardStore";
import { toast } from "@/utils/toast";

const boardStore = useBoardStore();

const name = ref("");
const color = ref("primary");
const saving = ref(false);
let inst = null;

const colors = [
  { id: "primaryColor",      value: "primary",      cls: "primary-clr" },
  { id: "brightblueColor",   value: "brightblue",   cls: "brightblue-clr" },
  { id: "lunargreenColor",   value: "lunargreen",   cls: "lunargreen-clr" },
  { id: "lavendarColor",     value: "lavendar",     cls: "lavendar-clr" },
  { id: "magentaColor",      value: "magenta",      cls: "magenta-clr" },
  { id: "chromeyellowColor", value: "chromeyellow", cls: "chromeyellow-clr" },
  { id: "lavaredColor",      value: "lavared",      cls: "lavared-clr" },
];

function getEl() {
  return document.getElementById("add_board");
}

function getInst() {
  const el = getEl();
  if (!el) return null;
  inst = Modal.getOrCreateInstance(el, { backdrop: true, keyboard: true });
  return inst;
}

function forceCloseAllModals() {
  // Safety net: clear any orphaned backdrops/body state
  document.querySelectorAll(".modal.show").forEach(m => {
    try { Modal.getOrCreateInstance(m).hide(); } catch {}
  });
  document.querySelectorAll(".modal-backdrop").forEach(b => b.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("paddingRight");
}

async function onSubmit() {
  if (!name.value) return toast("Board name is required", "error");
  try {
    saving.value = true;
    await boardStore.create({ name: name.value, color: color.value });

    // reset form
    name.value = "";
    color.value = "primary";

    // close modal
    getInst()?.hide();

    // If backdrop ever sticks (theme quirks), hard clean it up.
    setTimeout(() => {
      if (document.body.classList.contains("modal-open")) {
        forceCloseAllModals();
      }
    }, 150);
  } catch (e) {
    toast(e?.response?.data?.message || e.message || "Failed to create board", "error");
  } finally {
    saving.value = false;
  }
}

function onHidden() {
  // Final safety after close
  forceCloseAllModals();
}

onMounted(() => {
  const el = getEl();
  if (!el) return;

  // ensure single instance and hook hidden event
  inst = Modal.getOrCreateInstance(el);
  el.addEventListener("hidden.bs.modal", onHidden);

  // Debug helper for duplicate modals with same id
  const dupes = Array.from(document.querySelectorAll("#add_board"));
  if (dupes.length > 1) {
    console.warn("[tasks-board-modal] Duplicate #add_board modals found:", dupes.length);
  }
});

onBeforeUnmount(() => {
  const el = getEl();
  if (el) el.removeEventListener("hidden.bs.modal", onHidden);
});
</script>

<style scoped>
.visually-hidden {
  position: absolute !important;
  clip: rect(0,0,0,0) !important;
  clip-path: inset(50%) !important;
  height: 1px !important;
  width: 1px !important;
  overflow: hidden !important;
  white-space: nowrap !important;
  border: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
.ringed { outline: 2px solid var(--bs-primary); outline-offset: 2px; border-radius: 9999px; }
.theme-colorsset > label { display: inline-block; width: 24px; height: 24px; border-radius: 9999px; cursor: pointer; }
</style>
